import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { Textarea } from "@/components/ui/textarea";
import { useMember } from "@/contexts/memberContext";
import { useNewConversation } from "@/hooks/useNewConversation";
import { getInitials } from "@/lib/utils";
import { isElevatedUser } from "@/utils/userPermissions";
import { Loader2, X } from "lucide-react";
import type { MessageableEntity } from "@/types/MessageableEntity";

type NewConversationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSendMessage: (params: {
    type: "member" | "group";
    recipientId?: string;
    locationIds?: string[];
    content: string;
  }) => Promise<void>;
  initialEntity?: MessageableEntity | null;
};

const NewConversationDialog = ({ open, onOpenChange, onSendMessage, initialEntity }: NewConversationDialogProps) => {
  const {
    searchTerm,
    searchResults,
    selectedEntity,
    message,
    isSearching,
    isSending,
    isSendDisabled,
    setSearchTerm,
    setMessage,
    handleEntitySelect,
    handleRemoveSelection,
    handleSend,
    resetState,
  } = useNewConversation(onSendMessage, initialEntity);

  const { member: currentMember } = useMember();
  const canSeeMemberEmails = isElevatedUser(currentMember);

  const handleSendAndClose = async () => {
    const success = await handleSend();
    if (success) {
      resetState();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="text-3xl">New Conversation</DialogTitle>
          <DialogDescription>Search for a member, group or community and send a message.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Entity Selection (Member or Group) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">To:</label>
            {selectedEntity ? (
              <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
                <Item variant="outline" className="w-full bg-white">
                  <ItemContent>
                    <div className="flex flex-row items-center justify-between">
                      <div className="flex flex-row items-center space-x-3">
                        {selectedEntity.type === "member" ? (
                          <>
                            <Avatar className="w-10 h-10">
                              <AvatarImage
                                src={
                                  selectedEntity.avatar ||
                                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                }
                                alt="User Avatar"
                              />
                              <AvatarFallback>
                                {getInitials(`${selectedEntity.firstName} ${selectedEntity.lastName}`)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <ItemTitle className="text-base font-semibold">
                                {selectedEntity.firstName} {selectedEntity.lastName}
                              </ItemTitle>
                              {canSeeMemberEmails && (
                                <ItemDescription>
                                  <span>{selectedEntity.email}</span>
                                </ItemDescription>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            {selectedEntity.avatarUrl ? (
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={selectedEntity.avatarUrl} alt={selectedEntity.name} />
                                <AvatarFallback className="bg-indigo-100 text-indigo-600">
                                  {selectedEntity.memberCount}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                <span className="text-indigo-600 font-semibold">{selectedEntity.memberCount}</span>
                              </div>
                            )}
                            <div className="flex flex-col">
                              <ItemTitle className="text-base font-semibold">{selectedEntity.name}</ItemTitle>
                              <ItemDescription>
                                <span className="text-xs text-gray-500">Group</span>
                              </ItemDescription>
                            </div>
                          </>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveSelection}
                        className="h-8 w-8 p-0"
                        aria-label="Remove selection"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </ItemContent>
                </Item>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Input
                    placeholder="Search for a practice manager, group or member of staff..."
                    aria-label="Search for a practice manager, group or member of staff"
                    className="w-full h-12"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                      <X className="w-4 h-4 text-gray-400" onClick={() => setSearchTerm("")} />
                    </div>
                  )}
                </div>

                {/* Search Results */}
                <div className="relative">
                  {isSearching && <div className="mt-2 text-sm text-gray-500">Searching...</div>}
                  {!isSearching && searchResults.length > 0 && (
                    <div className="absolute w-full max-h-64 overflow-y-auto z-10 mt-1 bg-white rounded-lg border border-gray-200 shadow-md">
                      <ul className="p-2">
                        {searchResults.map((entity) => {
                          if (entity.type === "member") {
                            const fullName = `${entity.firstName} ${entity.lastName}`;
                            const initials = getInitials(fullName);
                            return (
                              <div key={entity.id} className="mb-1" onClick={() => handleEntitySelect(entity)}>
                                <Item
                                  variant="outline"
                                  className="w-full bg-gray-50 hover:bg-indigo-100 hover:cursor-pointer"
                                >
                                  <ItemContent>
                                    <div className="flex flex-row items-center space-x-4">
                                      <Avatar className="w-10 h-10">
                                        <AvatarImage
                                          src={
                                            entity.avatar ||
                                            "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                          }
                                          alt="User Avatar"
                                        />
                                        <AvatarFallback>{initials}</AvatarFallback>
                                      </Avatar>
                                      <div className="flex flex-col">
                                        <ItemTitle className="text-base font-semibold">{fullName}</ItemTitle>
                                        <ItemDescription className="flex flex-col">
                                          {canSeeMemberEmails && <span className="pb-0.5">{entity.email}</span>}
                                          {entity.practiceManagerLocationNames &&
                                            entity.practiceManagerLocationNames.length > 0 && (
                                              <span className="text-xs text-gray-500">
                                                Practice Manager for {entity.practiceManagerLocationNames}
                                              </span>
                                            )}
                                        </ItemDescription>
                                      </div>
                                    </div>
                                  </ItemContent>
                                </Item>
                              </div>
                            );
                          } else {
                            // Group entity
                            return (
                              <div key={entity.id} className="mb-1" onClick={() => handleEntitySelect(entity)}>
                                <Item
                                  variant="outline"
                                  className="w-full bg-gray-50 hover:bg-indigo-100 hover:cursor-pointer"
                                >
                                  <ItemContent>
                                    <div className="flex flex-row items-center space-x-4">
                                      {entity.avatarUrl ? (
                                        <Avatar className="w-10 h-10">
                                          <AvatarImage src={entity.avatarUrl} alt={entity.name} />
                                          <AvatarFallback className="bg-indigo-100 text-indigo-600">
                                            {entity.memberCount}
                                          </AvatarFallback>
                                        </Avatar>
                                      ) : (
                                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                          <span className="text-indigo-600 font-semibold text-sm">
                                            {entity.memberCount}
                                          </span>
                                        </div>
                                      )}
                                      <div className="flex flex-col">
                                        <ItemTitle className="text-base font-semibold">{entity.name}</ItemTitle>
                                        <ItemDescription>
                                          <span className="text-xs text-gray-500">
                                            Group - {entity.memberCount} members
                                          </span>
                                        </ItemDescription>
                                      </div>
                                    </div>
                                  </ItemContent>
                                </Item>
                              </div>
                            );
                          }
                        })}
                      </ul>
                    </div>
                  )}
                  {!isSearching && searchTerm && searchResults.length === 0 && (
                    <div className="mt-2 text-sm text-gray-400">No members or groups found.</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Message:</label>
            <Textarea
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[120px]"
              disabled={!selectedEntity}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetState();
              onOpenChange(false);
            }}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button onClick={handleSendAndClose} disabled={isSendDisabled}>
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Message"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationDialog;
